// ABOUTME: Debug overlay component for monitoring storage mechanisms and Web Worker status
// ABOUTME: Provides real-time validation of storage operations and system status

import React, { useState, useEffect, useCallback } from 'react';
import { StorageProvider, StorageProviderType } from '../services/storage/types';
import { initTracker, InitEvent } from '../services/storage/initTracker';
import { seedData, clearData } from '../services/storage';
import { StorageManager } from '../services/storage/StorageManager';

interface DebugInfo {
    storageProvider: string;
    isWorkerBased: boolean;
    opfsSupported: boolean;
    createWritableSupported: boolean;
    createSyncAccessHandleSupported: boolean;
    workerStatus: 'initializing' | 'ready' | 'error' | 'unknown';
    lastSaveMethod: string;
    lastLoadMethod: string;
    currentPersistenceMethod: string;
    persistenceOperations: number;
    databaseSize: number;
    operationCount: number;
    memoryUsage: string;
    jsHeapSize: number;
    errors: string[];
}

interface DebugOverlayProps {
    isVisible: boolean;
    provider: StorageProvider | null;
    isMinimized: boolean;
    onMinimizeToggle: (minimized: boolean) => void;
    onDataSeeded: () => void;
    onDataCleared?: () => void;
    onProviderChange?: (provider: StorageProviderType) => void;
}

const DebugOverlay: React.FC<DebugOverlayProps> = ({ isVisible, provider, isMinimized, onMinimizeToggle, onDataSeeded, onDataCleared, onProviderChange }) => {
    const [debugInfo, setDebugInfo] = useState<DebugInfo>({
        storageProvider: 'Unknown',
        isWorkerBased: false,
        opfsSupported: false,
        createWritableSupported: false,
        createSyncAccessHandleSupported: false,
        workerStatus: 'unknown',
        lastSaveMethod: 'None',
        lastLoadMethod: 'None',
        currentPersistenceMethod: 'Unknown',
        persistenceOperations: 0,
        databaseSize: 0,
        operationCount: 0,
        memoryUsage: 'N/A',
        jsHeapSize: 0,
        errors: []
    });
    const [initEvents, setInitEvents] = useState<InitEvent[]>([]);
    
    // Data control states
    const [isSeeding, setIsSeeding] = useState(false);
    const [seedError, setSeedError] = useState<string | null>(null);
    const [seedDays, setSeedDays] = useState<number>(180);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Data control functions
    const handleSeed = useCallback(async () => {
        setIsSeeding(true);
        setSeedError(null);
        try {
            await seedData(500, seedDays);
            onDataSeeded();
        } catch (err) {
            console.error("Failed to seed data:", err);
            const errorMessage = err instanceof Error ? err.message : "An unknown error occurred.";
            setSeedError(`Failed to seed data: ${errorMessage}`);
        } finally {
            setIsSeeding(false);
        }
    }, [onDataSeeded, seedDays]);

    const handleDeleteAllData = async () => {
        setIsDeleting(true);
        setSeedError(null);
        try {
            await clearData();
            onDataCleared?.();
            setShowConfirmDialog(false);
        } catch (error) {
            console.error('Failed to delete all data:', error);
            setSeedError(`Failed to delete data: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setIsDeleting(false);
        }
    };

    // Subscribe to init events
    useEffect(() => {
        const unsubscribe = initTracker.subscribe((events) => {
            setInitEvents(events);
            
            // Update persistence info from events
            const persistenceEvents = events.filter(e => e.type === 'persistence');
            if (persistenceEvents.length > 0) {
                const lastPersistenceEvent = persistenceEvents[persistenceEvents.length - 1];
                const details = lastPersistenceEvent.details;
                
                setDebugInfo(prev => ({
                    ...prev,
                    currentPersistenceMethod: details?.method || 'Unknown',
                    persistenceOperations: persistenceEvents.length,
                    lastSaveMethod: details?.operation === 'save' ? details.method : prev.lastSaveMethod,
                    lastLoadMethod: details?.operation === 'load' ? details.method : prev.lastLoadMethod
                }));
            }
        });

        // Get initial events
        setInitEvents(initTracker.getEvents());

        return unsubscribe;
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        const updateDebugInfo = async () => {
            try {
                // Get provider info
                const providerName = provider?.getName() || 'Unknown';
                const isWorkerBased = providerName.includes('Worker');
                
                // Determine worker status based on provider state
                let workerStatus: 'initializing' | 'ready' | 'error' | 'unknown' = 'unknown';
                if (isWorkerBased && provider) {
                    // Check if provider has initialized flag
                    const providerAny = provider as any;
                    if (providerAny.initialized === true) {
                        workerStatus = 'ready';
                    } else if (providerAny.initialized === false && providerAny.initializationPromise) {
                        workerStatus = 'initializing';
                    }
                }

                // Check OPFS support
                const opfsSupported = 'storage' in navigator && 'getDirectory' in navigator.storage;
                
                let createWritableSupported = false;
                let createSyncAccessHandleSupported = false;
                
                if (opfsSupported) {
                    try {
                        const root = await navigator.storage.getDirectory();
                        const testHandle = await root.getFileHandle('debug_test', { create: true });
                        
                        createWritableSupported = typeof testHandle.createWritable === 'function';
                        createSyncAccessHandleSupported = typeof testHandle.createSyncAccessHandle === 'function';
                        
                        // Clean up test file
                        await root.removeEntry('debug_test');
                    } catch (error) {
                        console.log('Debug: OPFS test failed:', error);
                    }
                }

                // Get storage info
                let databaseSize = 0;
                try {
                    const storageInfo = await provider?.getStorageInfo();
                    databaseSize = storageInfo?.used || 0;
                } catch (error) {
                    console.log('Debug: Failed to get storage info:', error);
                }

                // Get memory usage
                let memoryUsage = 'N/A';
                let jsHeapSize = 0;
                try {
                    const performance = window.performance as any;
                    if (performance && performance.memory) {
                        jsHeapSize = performance.memory.usedJSHeapSize;
                        memoryUsage = `${(jsHeapSize / 1024 / 1024).toFixed(1)} MB`;
                    } else {
                        memoryUsage = 'N/A (API not supported)';
                    }
                } catch (error) {
                    console.log('Debug: Failed to get memory info:', error);
                }

                setDebugInfo(prev => ({
                    ...prev,
                    storageProvider: providerName,
                    isWorkerBased,
                    opfsSupported,
                    createWritableSupported,
                    createSyncAccessHandleSupported,
                    databaseSize,
                    workerStatus,
                    memoryUsage,
                    jsHeapSize
                }));
            } catch (error) {
                console.error('Debug: Failed to update debug info:', error);
                setDebugInfo(prev => ({
                    ...prev,
                    errors: [...prev.errors.slice(-4), `Update failed: ${error instanceof Error ? error.message : String(error)}`]
                }));
            }
        };

        // Update immediately
        updateDebugInfo();

        // Update every 5 seconds
        const interval = setInterval(updateDebugInfo, 5000);

        return () => clearInterval(interval);
    }, [isVisible, provider]);


    if (!isVisible) return null;

    return (
        <div className="fixed top-4 right-4 bg-slate-800 border border-slate-600  text-xs font-mono z-50 shadow-lg" style={{ maxWidth: isMinimized ? '240px' : '480px' }}>
            <div className="flex items-center justify-between p-4 pb-3">
                <div className="flex items-center gap-2">
                    <h3 className="text-slate-200 font-semibold">Storage Debug</h3>
                </div>
                <button
                    onClick={() => onMinimizeToggle(!isMinimized)}
                    className="text-slate-400 hover:text-slate-200 transition-colors text-xs px-2 py-1  border border-slate-600 hover:border-slate-500"
                >
                    {isMinimized ? 'Expand' : 'Minimize'}
                </button>
            </div>
            
            {!isMinimized && (
                <div className="px-4 pb-4">
            
            <div className="space-y-2 text-slate-300">
                <div className="flex justify-between">
                    <span>Provider:</span>
                    <span className="text-blue-400">{debugInfo.storageProvider}</span>
                </div>
                
                <div className="flex justify-between">
                    <span>Worker-based:</span>
                    <span className={debugInfo.isWorkerBased ? 'text-green-400' : 'text-yellow-400'}>
                        {debugInfo.isWorkerBased ? 'Yes' : 'No'}
                    </span>
                </div>
                
                {debugInfo.isWorkerBased && (
                    <div className="flex justify-between">
                        <span>Worker Status:</span>
                        <span className={
                            debugInfo.workerStatus === 'ready' ? 'text-green-400' :
                            debugInfo.workerStatus === 'initializing' ? 'text-yellow-400' :
                            debugInfo.workerStatus === 'error' ? 'text-red-400' :
                            'text-gray-400'
                        }>
                            {debugInfo.workerStatus}
                        </span>
                    </div>
                )}
                
                <div className="border-t border-slate-600 pt-2">
                    <div className="text-slate-400 mb-1">Browser Support:</div>
                    <div className="flex justify-between">
                        <span>OPFS:</span>
                        <span className={debugInfo.opfsSupported ? 'text-green-400' : 'text-red-400'}>
                            {debugInfo.opfsSupported ? 'Yes' : 'No'}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span>createWritable:</span>
                        <span className={debugInfo.createWritableSupported ? 'text-green-400' : 'text-red-400'}>
                            {debugInfo.createWritableSupported ? 'Yes' : 'No'}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span>createSyncAccessHandle:</span>
                        <span className={debugInfo.createSyncAccessHandleSupported ? 'text-green-400' : 'text-red-400'}>
                            {debugInfo.createSyncAccessHandleSupported ? 'Yes' : 'No'}
                        </span>
                    </div>
                </div>
                
                <div className="border-t border-slate-600 pt-2">
                    <div className="text-slate-400 mb-1">Persistence Method:</div>
                    <div className="flex justify-between">
                        <span>Current:</span>
                        <span className={`font-semibold ${
                            debugInfo.currentPersistenceMethod.includes('createSyncAccessHandle') ? 'text-green-400' :
                            debugInfo.currentPersistenceMethod.includes('createWritable') ? 'text-blue-400' :
                            debugInfo.currentPersistenceMethod.includes('localStorage') ? 'text-yellow-400' :
                            'text-gray-400'
                        }`}>
                            {debugInfo.currentPersistenceMethod}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span>Operations:</span>
                        <span className="text-cyan-400">{debugInfo.persistenceOperations}</span>
                    </div>
                </div>
                
                <div className="border-t border-slate-600 pt-2">
                    <div className="text-slate-400 mb-1">Last Operations:</div>
                    <div className="flex justify-between">
                        <span>Save:</span>
                        <span className="text-purple-400">{debugInfo.lastSaveMethod}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Load:</span>
                        <span className="text-purple-400">{debugInfo.lastLoadMethod}</span>
                    </div>
                </div>
                
                <div className="border-t border-slate-600 pt-2">
                    <div className="text-slate-400 mb-1">Storage & Memory:</div>
                    <div className="flex justify-between">
                        <span>DB Size:</span>
                        <span className="text-cyan-400">
                            {debugInfo.databaseSize > 0 ? `${(debugInfo.databaseSize / 1024).toFixed(1)}KB` : '0KB'}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span>JS Heap:</span>
                        <span className="text-cyan-400">{debugInfo.memoryUsage}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Operations:</span>
                        <span className="text-cyan-400">{debugInfo.operationCount}</span>
                    </div>
                </div>
                
                {debugInfo.errors.length > 0 && (
                    <div className="border-t border-slate-600 pt-2">
                        <div className="text-red-400 mb-1">Recent Errors:</div>
                        <div className="space-y-1 max-h-20 overflow-y-auto">
                            {debugInfo.errors.slice(-3).map((error, index) => (
                                <div key={index} className="text-red-300 text-xs break-words">
                                    {error}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Data Controls */}
                <div className="border-t border-slate-600 pt-2">
                    <div className="text-slate-400 mb-2">Data Controls:</div>
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <select
                                value={seedDays}
                                onChange={(e) => setSeedDays(Number(e.target.value))}
                                className="px-2 py-1 bg-slate-700 text-slate-200 border border-slate-600 text-xs flex-1"
                                disabled={isSeeding || isDeleting}
                            >
                                <option value={30}>30 days</option>
                                <option value={60}>60 days</option>
                                <option value={90}>90 days</option>
                                <option value={180}>180 days</option>
                                <option value={360}>360 days</option>
                            </select>
                            <button
                                onClick={handleSeed}
                                disabled={isSeeding || isDeleting}
                                className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSeeding ? 'Seeding...' : 'Seed Data'}
                            </button>
                        </div>
                        <button
                            onClick={() => setShowConfirmDialog(true)}
                            disabled={isSeeding || isDeleting}
                            className="w-full px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete All Data'}
                        </button>
                        {seedError && <p className="text-red-400 text-xs">{seedError}</p>}
                    </div>
                </div>
                
                {/* Logs */}
                <div className="border-t border-slate-600 pt-2">
                    <div className="text-slate-400 mb-1">Logs:</div>
                    <div className="space-y-1 max-h-40 overflow-y-auto bg-slate-900  p-2">
                        {initEvents.length === 0 ? (
                            <div className="text-slate-500">No log events yet</div>
                        ) : (
                            initEvents
                                .filter(event => {
                                    // Reduce noise by filtering out certain verbose events
                                    if (event.type === 'info' && event.message.includes('Debug:')) return false;
                                    if (event.message.includes('Worker: Handling message type:')) return false;
                                    if (event.message.includes('Worker: Database loaded from')) return false;
                                    if (event.message.includes('Worker: Database saved to')) return false;
                                    return true;
                                })
                                .slice(-20) // Show only last 20 events
                                .map((event, index) => {
                                const time = new Date(event.timestamp).toLocaleTimeString();
                                const colorClass = 
                                    event.type === 'error' ? 'text-red-400' :
                                    event.type === 'warning' ? 'text-yellow-400' :
                                    event.type === 'success' ? 'text-green-400' :
                                    event.type === 'persistence' ? 'text-purple-400' :
                                    event.type === 'sql' ? 'text-orange-400' :
                                    'text-blue-400';
                                
                                return (
                                    <div key={index} className="flex items-start gap-2 text-xs">
                                        <span className="text-slate-500 flex-shrink-0">{time}</span>
                                        <span className={`${colorClass} flex-shrink-0`}>[{event.component}]</span>
                                        <span className="text-slate-300">{event.message}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
                </div>
                </div>
            )}
            
            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-800 p-6 max-w-md w-full border border-slate-600">
                        <div className="flex items-center space-x-3 mb-4">
                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <h3 className="text-lg font-semibold text-white">Delete All Data</h3>
                        </div>
                        
                        <p className="text-slate-300 mb-6">
                            Are you sure you want to delete all data? This action cannot be undone and will permanently remove all workouts, meals, and profile information.
                        </p>
                        
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowConfirmDialog(false)}
                                disabled={isDeleting}
                                className="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteAllData}
                                disabled={isDeleting}
                                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete All Data'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DebugOverlay;